import {
    Iteration,
    ProjectElementNode,
} from '@deltek/specification-client/core/Models/productListingDetail'
import { ProjectElementType } from '@deltek/specification-client/core/Models/projectElement'
import { isEqual } from '@deltek/specification-client/core/Utilities/objectUtils'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { ElementType } from 'api/graphQL/reactiveVars/element'
import { useElementTreeStore } from 'components/ElementsTree/ElementsTreeStore'
import { updatedElementTitle } from 'components/ElementsTree/utils'
import {
    BulkAddElementInput,
    BulkAddElementInputWithMetadata,
    NodeSearchFilter,
} from 'models/getProjectElementNodesInput'
import { FamilyIteration, ProductFamilies } from 'models/projectElementNode'
import {
    CheckForDuplicateFilesInAssemblyInput,
    ImportUploadFileInfo,
} from 'models/projectImport'
import { FamilyNumberFormat } from 'models/projectSettings'
import { getFormattedFamilyNumber } from 'pages/ProjectsPage/projectUtils'
import React from 'react'
import { useLocation } from 'react-router-dom'
import Autosizer from 'react-virtualized-auto-sizer'
import { VariableSizeTree } from 'react-vtree'
import { AreaContext } from '../Common/AreaContext'
import OMVersionDropdown from '../Common/OMVersionDropdown'
import { getNodeById, isItemExist } from '../Common/utils'
import { WorkspaceActionBarContext } from '../WorkspaceActionBarContext'
import { elementsPanelSearchBarMargin } from './elementCardUtil'
import ElementTreeViewNode, { TreeNodeProps } from './ElementTreeViewNode'
export interface ElementTreeViewStyleProps {
    calledFromImportDuplicateFamily?: boolean
    calledFromImportPage?: boolean
    contentTeamFromStudioPage?: boolean
    isBetaWorkspaceToggleEnabled?: boolean
}

const useElementTreeViewStyles = makeStyles((theme: Theme) => ({
    elementPanelContentContainer: {
        height: '100%',
        '& > div > div': {
            overflowX: 'hidden !important',
            width: (props: ElementTreeViewStyleProps) =>
                props?.calledFromImportPage ? '465px !important' : 'inherit',
            height: (props: ElementTreeViewStyleProps) =>
                props?.calledFromImportDuplicateFamily
                    ? '330px !important'
                    : '100%',
        },
    },
    filterContainer: (props: ElementTreeViewStyleProps) => ({
        width: props.isBetaWorkspaceToggleEnabled ? 'auto' : pxToRem(300),
        margin: theme.spacing(
            0,
            props.isBetaWorkspaceToggleEnabled ? 2 : 0,
            3.25,
            1.25
        ),
        position: 'relative',
        top: pxToRem(10),
        '& > div': {
            overflowX: 'visible',
        },
    }),
}))

let hiddenElementSizer: HTMLSpanElement | null = null
export interface ElementItemDataNode {
    /** A rendering ID derived from the tree path */
    id?: string

    parentNodeId?: string
    selected?: boolean
    title?: string
    subtitle?: string
    expanded?: boolean
    isInProject?: boolean
    hasSubItems?: boolean
    baseElementId?: string
    children?: ElementItemDataNode[]

    /** The ID of the ProjectElementNode this node represents */
    elementId?: string
    treePath?: string
    elementType?: string
    isSearchResult?: boolean
    isL4L5Assembly?: boolean
    isLastSubAssembly?: boolean
    isBestPractice?: boolean
    isGlobalBestPractice?: boolean
    isProjectOnly?: boolean
    isImportedToProject?: boolean
    importedToProjectId?: string
    isSelectedForAssociation?: boolean
    isAddedForAssociation?: boolean
    hasChildAssociation?: boolean
    hasSiblingAssociation?: boolean
    emptyWithoutAssociations?: boolean
    isBasedFromImportedNode?: boolean
    selectedElementFormat?: string
    disableExpand?: boolean
    isBasedFromManufacturerOption?: boolean
    isBpPublished?: boolean

    /** Determines if this node is selected during importing of duplicate family versions */
    isDuplicateFamilyVersionSelected?: boolean
    updateElementList?: (elementList: ProjectElementNode[]) => void
    elementAddedToProjectId?: string
    iteration?: Iteration
    isDeactivated?: boolean
    isFromPPLTemplate?: boolean
}

export interface ParentElementItemNode {
    id: string
    elementId: string
    familyViewElementId: string
    childrenIds: string[]
}
export interface ElementItemIdentifier {
    treePath: string
    baseElementId: string
    elementType?: string
    isLastSubAssembly?: string
    selectedLeafNodes?: string[]
    nodeId?: string
    id?: string
    isBpPublished?: boolean
}

export interface StackElement {
    nestingLevel: number
    node: ElementItemDataNode
}

export interface ElementTreeViewProps extends ElementTreeViewStyleProps {
    selectedElementIdentifier?: ElementItemIdentifier
    onSelectedElementIdentifierChange?: (
        newSelectedElementIdentifier: ElementItemIdentifier
    ) => void
    savePreviousElementForPersistance?: (
        elementIdentifier?: ElementItemIdentifier
    ) => void
    sourceTree?: ElementItemDataNode[]
    onLoadMoreClick?: VoidFunction

    /** Set this to the ID of the node a user shift+clicked on previously
     * to trigger a bulk select of that node and its children.
     * We use this prop to support bulk add of nodes whose children haven't loaded yet.
     */
    parentNodeIdPendingBulkSelect?: string

    handleBPElementTreeChange?: (
        id: string,
        treepath: string,
        isBP: boolean,
        isGBP: boolean,
        remove: boolean
    ) => void
    showLoadMoreButton?: boolean
    isLoading?: boolean
    totalElementCount?: number
    totalFetchedElementCount?: number
    showFullLoadState?: boolean
    isNewDataLoading?: boolean
    famNumberFormat: FamilyNumberFormat
    isCollapsed?: boolean
    searchFilters?: NodeSearchFilter[]
    onLoadChildren: (id: string, isBulkSelectRequested?: boolean) => void
    handleChangeAssociation?: (element: ElementItemDataNode) => void
    associationList?: CheckForDuplicateFilesInAssemblyInput[]
    selectedFile?: Partial<ImportUploadFileInfo> | null
    hasChildAssociation?: boolean
    hasSiblingAssociation?: boolean
    selectedElementFormat?: string
    disableExpand?: boolean
    fromStudio?: boolean
    selectedBulkAddElements?: BulkAddElementInput[]
    setSelectedBulkAddElements?: (
        bulkElements: BulkAddElementInput[] | undefined
    ) => void
    projectId?: string
    isFromWorkSpacePage?: boolean
    persistedElement?: ElementItemIdentifier
    handleSelectDuplicateFamilyVersion?: (element: ElementItemDataNode) => void
    selectedDuplicateFamilyVersions?: ElementItemDataNode[]
    handleOMVersionSelectChange?: (
        values: ProductFamilies[] | FamilyIteration
    ) => void
    isOMWorkspace?: boolean
    selectedOMVersion?: FamilyIteration | undefined
    omDropdownOptions?: FamilyIteration[] | []
    updateElementList?: (elementList: ProjectElementNode[]) => void
    isBetaWorkspaceToggleEnabled?: boolean
}

const ElementTreeView: React.FC<ElementTreeViewProps> = ({
    selectedElementIdentifier,
    onSelectedElementIdentifierChange,
    savePreviousElementForPersistance,
    sourceTree = [],
    onLoadMoreClick,
    parentNodeIdPendingBulkSelect,
    handleBPElementTreeChange,
    showLoadMoreButton,
    isLoading,
    totalElementCount = 0,
    totalFetchedElementCount = 0,
    showFullLoadState = false,
    isNewDataLoading = false,
    famNumberFormat,
    isCollapsed,
    searchFilters,
    onLoadChildren,
    calledFromImportPage,
    handleChangeAssociation,
    associationList,
    selectedFile,
    selectedElementFormat,
    contentTeamFromStudioPage,
    disableExpand = false,
    fromStudio,
    selectedBulkAddElements,
    setSelectedBulkAddElements,
    projectId,
    isFromWorkSpacePage = false,
    persistedElement,
    calledFromImportDuplicateFamily,
    handleSelectDuplicateFamilyVersion,
    selectedDuplicateFamilyVersions,
    handleOMVersionSelectChange,
    isOMWorkspace,
    selectedOMVersion,
    omDropdownOptions,
    updateElementList,
    isBetaWorkspaceToggleEnabled = false,
}) => {
    const { currentState } = React.useContext(WorkspaceActionBarContext)

    const ELEMENT_WIDTH_MO_WITH_BP = '75%'
    const ELEMENT_WIDTH_NO_MO_WITH_BP = '80%'
    const ELEMENT_WIDTH_MO_WITHOUT_BP = '88%'
    const ELEMENT_WIDTH_FULL = '100%'

    const classes = useElementTreeViewStyles({
        calledFromImportPage: calledFromImportPage,
        calledFromImportDuplicateFamily: calledFromImportDuplicateFamily,
        isBetaWorkspaceToggleEnabled,
    })
    const { selectedElementId, persistState } = React.useContext(AreaContext)

    const [hasScrolledIntoElement, setHasScrolledIntoElement] = React.useState(
        false
    )
    const { pathname } = useLocation()
    const isSearchBarDisplayed = useElementTreeStore(
        (store) => store.isSearchBarDisplayed
    )
    const isAddedToProjectFilterActive = React.useMemo(
        () => currentState?.['elementsInProjectFilterWidgetActive'],
        [currentState]
    )
    const virtualTreeRef = React.useRef<VariableSizeTree<TreeNodeProps>>(null)
    /* Remove extra height from element tree because of version control menu */
    const treeHeightForVersionControl = React.useMemo(
        () =>
            elementsPanelSearchBarMargin(
                isOMWorkspace,
                !isOMWorkspace && contentTeamFromStudioPage
            ) +
            // resize element tree container for bulk add bar container
            (!!selectedBulkAddElements && selectedBulkAddElements?.length !== 0
                ? 70
                : 0) +
            // resize element tree container for added to project filter container
            (isAddedToProjectFilterActive ? 50 : 0),

        [
            isOMWorkspace,
            isSearchBarDisplayed,
            contentTeamFromStudioPage,
            selectedBulkAddElements,
            isAddedToProjectFilterActive,
        ]
    )

    const treeArray: ElementItemDataNode[] = React.useMemo(() => {
        if (isLoading) {
            return (showFullLoadState ? [] : sourceTree).concat(
                Array.from(new Array(3)).map((_, index: number) => ({
                    id: `loadSkeleton-${index}`,
                }))
            )
        }
        if (showLoadMoreButton) {
            return sourceTree.concat([
                {
                    id: `loadMoreButton|${totalFetchedElementCount}|${totalElementCount}`,
                },
            ])
        }

        return sourceTree
    }, [
        sourceTree,
        isLoading,
        showLoadMoreButton,
        totalElementCount,
        totalFetchedElementCount,
        showFullLoadState,
    ])

    // The below callback is used to persist the nearest addedToProject element
    const persistPreviousAddedToProjectElement = React.useCallback(
        (element: ElementItemDataNode) => {
            if (!element.isInProject) {
                const parentIds = element?.id?.split('||')
                const parentElement: ElementItemDataNode | undefined =
                    parentIds?.length && parentIds?.length > 1
                        ? treeArray.find((item) => item.id == parentIds?.[0])
                        : element
                let itemToPersist: ElementItemIdentifier | undefined
                if (parentElement?.isInProject) {
                    itemToPersist = {
                        baseElementId: parentElement.baseElementId ?? '',
                        treePath: parentElement.id ?? '',
                        id: parentElement?.elementId,
                        elementType: parentElement?.elementType ?? '',
                    }
                } else {
                    const parentIndex = treeArray.findIndex(
                        (item) => item.elementId === parentElement?.elementId
                    )
                    if (parentIndex >= 0) {
                        const splitarray = [...treeArray]
                            .slice(0, parentIndex)
                            .reverse()
                        const previousAddedToProjectParent = splitarray.find(
                            (item) => item.isInProject
                        )
                        itemToPersist = previousAddedToProjectParent
                            ? {
                                  baseElementId:
                                      previousAddedToProjectParent?.baseElementId ??
                                      '',
                                  treePath:
                                      previousAddedToProjectParent?.id ?? '',
                                  id: previousAddedToProjectParent?.elementId,
                                  elementType:
                                      previousAddedToProjectParent?.elementType ??
                                      '',
                              }
                            : undefined
                    } else {
                        itemToPersist = undefined
                    }
                }
                savePreviousElementForPersistance?.(itemToPersist)
            } else {
                savePreviousElementForPersistance?.({
                    baseElementId: element.baseElementId ?? '',
                    treePath: element.id ?? '',
                    id: element?.elementId,
                    elementType: element?.elementType ?? '',
                })
            }
        },
        [treeArray, savePreviousElementForPersistance]
    )

    const handleElementItemCardClick = React.useCallback(
        (element: ElementItemDataNode) => {
            if (
                contentTeamFromStudioPage &&
                element?.elementType === 'PRODUCTTYPE'
            ) {
                onSelectedElementIdentifierChange?.({
                    baseElementId: element.baseElementId ?? '',
                    treePath: element.id ?? '',
                    id: element?.elementId,
                    elementType: element?.elementType ?? '',
                })
                persistPreviousAddedToProjectElement(element)
            } else if (
                contentTeamFromStudioPage &&
                element?.elementType === 'PRODUCTFAMILY' &&
                element?.elementId === selectedElementIdentifier?.id
            ) {
                const familyWrapper = document.getElementById('droppable-area')
                if (familyWrapper!.scrollTop) {
                    setTimeout(function () {
                        familyWrapper!.scrollTop = 0
                    }, 10)
                }
            } else {
                if (calledFromImportDuplicateFamily) {
                    handleSelectDuplicateFamilyVersion?.(element)
                }
                if (calledFromImportPage) {
                    handleChangeAssociation?.(element)
                }
                persistState?.('selectedTopicData', undefined)
                onSelectedElementIdentifierChange?.({
                    baseElementId: element.baseElementId ?? '',
                    treePath: element.id ?? '',
                    id: element?.elementId,
                    elementType: element?.elementType ?? '',
                    isBpPublished: element?.isBpPublished,
                })
                persistPreviousAddedToProjectElement(element)
                setHasScrolledIntoElement(true)
            }
        },
        [
            onSelectedElementIdentifierChange,
            calledFromImportPage,
            handleChangeAssociation,
            persistState,
            contentTeamFromStudioPage,
            selectedElementIdentifier,
            persistPreviousAddedToProjectElement,
            setHasScrolledIntoElement,
        ]
    )

    const removeRepeatingElements = React.useCallback(
        (arr: BulkAddElementInput[]) => {
            return arr.reduce(
                (prev: BulkAddElementInput[], curr: BulkAddElementInput) => {
                    if (
                        !prev.some(
                            (a) =>
                                a.nodeId === curr.nodeId ||
                                a.treePath === curr.treePath
                        )
                    )
                        prev.push(curr)
                    return prev
                },
                []
            )
        },
        []
    )

    const getChildNodeItems: any = React.useCallback(
        (items?: ElementItemDataNode[]) => {
            return items
                ?.flatMap((item: ElementItemDataNode) =>
                    item.children?.length
                        ? [item, ...getChildNodeItems(item.children)]
                        : item
                )
                .filter((node) => node.baseElementId !== 'none')
        },
        []
    )

    const convertNodeElementIntoPayloadInputItem = React.useCallback(
        (item: ElementItemDataNode) => {
            return {
                elementType: item.elementType,
                baseElementId: item.baseElementId,
                nodeId: item.elementId,
                projectId: projectId,
                treePath: item.id,
                isProductFamilyGroupView: selectedElementFormat === 'DIVISIONS',
                isBasedFromManufacturerOption:
                    item.isBasedFromManufacturerOption,
            }
        },
        [selectedElementFormat]
    )
    const handleBulkAddRemoveElement = React.useCallback(
        (element: ElementItemDataNode, type: String, isAdd: boolean) => {
            let items:
                | BulkAddElementInput[]
                | BulkAddElementInputWithMetadata[]
                | undefined = !!selectedBulkAddElements
                ? [...selectedBulkAddElements]
                : []

            // gets children items from the tree
            let childItemsToAddOrRemove =
                getChildNodeItems(element.children).map(
                    (item: ElementItemDataNode) => {
                        return convertNodeElementIntoPayloadInputItem(item)
                    }
                ) ?? []

            let currentElement: ElementItemDataNode = { ...element }

            // spliting treepath into id's to get the parent element treepath
            let parentIds = currentElement?.id?.split('||')

            if (parentIds?.length && parentIds?.length > 1) {
                const parentElement:
                    | ElementItemDataNode
                    | undefined = treeArray.find(
                    (item) => parentIds?.length && item.id == parentIds?.[0]
                )
                const parentChildItems: ElementItemDataNode[] = getChildNodeItems(
                    parentElement?.children
                )

                // generating exact tree paths of
                let parentIdsExact: string[] = []
                let lastId = ''
                parentIds.forEach((id) => {
                    if (lastId !== '') {
                        lastId += '||'
                    }
                    lastId += id
                    parentIdsExact.push(lastId)
                })
                // adding child to root parent elements
                const itemsToAdd = parentChildItems
                    .filter(
                        (item1: ElementItemDataNode) =>
                            item1.id && parentIdsExact.includes(item1.id)
                    )
                    .concat(parentElement ?? [])
                    .filter((node: ElementItemDataNode) => !node.isInProject)
                    .map((item: ElementItemDataNode) => {
                        return convertNodeElementIntoPayloadInputItem(item)
                    })

                items = items.concat(itemsToAdd)
                const productTypeAndFamilies: BulkAddElementInput[] = parentChildItems
                    .map((node: ElementItemDataNode) => {
                        return convertNodeElementIntoPayloadInputItem(node)
                    })
                    .filter(
                        (item) =>
                            item.elementType === 'PRODUCTFAMILY' &&
                            selectedBulkAddElements?.some(
                                (si) => si.nodeId == item.nodeId
                            ) &&
                            currentElement.elementType === 'PRODUCTFAMILY'
                    )
                if (!isAdd && productTypeAndFamilies.length === 1) {
                    childItemsToAddOrRemove = childItemsToAddOrRemove.concat(
                        itemsToAdd
                    )
                }
            }

            if (currentElement.elementId) {
                if (isAdd) {
                    items.push(
                        convertNodeElementIntoPayloadInputItem(currentElement)
                    )
                    if (type === 'all') {
                        items = items.concat(childItemsToAddOrRemove)
                    }
                } else {
                    items = items.filter(
                        (a: BulkAddElementInput) =>
                            !childItemsToAddOrRemove.some(
                                (item: BulkAddElementInput) =>
                                    item.nodeId === a.nodeId ||
                                    item.treePath === a.treePath
                            )
                    )
                    items = items.filter(
                        (a: BulkAddElementInput) =>
                            a.nodeId !== currentElement.elementId &&
                            a.treePath !== currentElement.id
                    )
                }
            }
            setSelectedBulkAddElements?.(removeRepeatingElements(items))
        },
        [
            selectedBulkAddElements,
            setSelectedBulkAddElements,
            removeRepeatingElements,
            getChildNodeItems,
            convertNodeElementIntoPayloadInputItem,
            treeArray,
        ]
    )

    const treeWalker = React.useCallback(
        function* (): Generator<string | TreeNodeProps> {
            const stack: StackElement[] = []

            /*
             * To be able to size the items properly,
             * we create a hidden element, insert the title text
             * into that element, and measure its height.
             *
             * This approach was suggested by the author of
             * react-vtree in the following link:
             *
             * https://github.com/Lodin/react-vtree/issues/16#issuecomment-581515423
             */
            if (!hiddenElementSizer) {
                hiddenElementSizer = document.createElement('span')
                hiddenElementSizer.setAttribute('tab-index', '-1')
                hiddenElementSizer.setAttribute('aria-hidden', 'true')
                hiddenElementSizer.setAttribute('id', 'hiddenElementSizer')
                hiddenElementSizer.style.position = 'absolute'
                hiddenElementSizer.style.lineHeight = '1.4'
            }

            for (let i = 0; i < treeArray.length; i++) {
                const tree = treeArray[i]
                stack.push({
                    nestingLevel: 0,
                    node: tree,
                })

                /**TODO: This is the temporary code changes for CKE ProductType elements once it will be available we can remove some of the conditions */
                const parentElementItemNodes: ParentElementItemNode[] = []

                while (stack.length > 0) {
                    const { node, nestingLevel } = stack.shift()!
                    let cardItemHeight = 53

                    node.isSelectedForAssociation =
                        associationList &&
                        associationList?.findIndex(
                            (item) =>
                                item?.treePath?.split(
                                    `||${selectedFile?.familyId?.replaceAll(
                                        /['"]+/g,
                                        ''
                                    )}`
                                )?.[0] === node.id
                        ) > -1
                            ? true
                            : false

                    const isOMWorkspacePTNode =
                        !!isOMWorkspace &&
                        node?.elementType === ElementType.PRODUCTTYPE

                    const calculatedTitleWidth = calculateTitleElementWidth(
                        !!node?.hasSubItems || !!isOMWorkspacePTNode,
                        nestingLevel,
                        node?.isBestPractice || node?.isGlobalBestPractice
                    )
                    // Transform element title to title case
                    const transformedElementTitle = isBetaWorkspaceToggleEnabled
                        ? updatedElementTitle(
                              node?.elementType!,
                              node.title,
                              node.subtitle,
                              isBetaWorkspaceToggleEnabled,
                              selectedElementFormat,
                              famNumberFormat
                          )
                        : node.title
                    if (transformedElementTitle) {
                        hiddenElementSizer.style.width = `${calculatedTitleWidth}px`
                        hiddenElementSizer.textContent =
                            transformedElementTitle ?? ''

                        if (hiddenElementSizer.parentNode === null) {
                            document.body.appendChild(hiddenElementSizer)
                        }
                        if (!isBetaWorkspaceToggleEnabled) {
                            cardItemHeight =
                                55 + hiddenElementSizer.clientHeight - 21
                        }
                    }
                    let transformedSubtitle = node?.subtitle ?? ''
                    if (node.elementType === ProjectElementType.PRODUCTFAMILY) {
                        const nameAndNumber = transformedSubtitle?.split(' ')
                        transformedSubtitle = `${
                            nameAndNumber?.[0]
                        } ${getFormattedFamilyNumber(
                            nameAndNumber?.[1] ?? '',
                            famNumberFormat
                        )}`
                    }

                    if (calledFromImportDuplicateFamily) {
                        // Use the subtitle formatted from the ElementsPanel as it is
                        transformedSubtitle = node?.subtitle ?? ''
                    }

                    //expand last family in the tree to show its all PT if its have,  Ref:- Bug #1891057
                    const childItemExist: boolean = isItemExist(
                        node,
                        selectedElementIdentifier?.id
                    )

                    const lastFamilyExpand =
                        (node.elementId === selectedElementIdentifier?.id ||
                            childItemExist) &&
                        node.hasSubItems

                    const isExpand =
                        !isNewDataLoading &&
                        ((node.id &&
                            (selectedElementIdentifier?.treePath?.startsWith(
                                node.id
                            ) ||
                                node?.children?.length! > 0) &&
                            selectedElementIdentifier?.treePath !== node.id) ||
                            (isCollapsed ?? true))

                    const isDuplicateFamilyVersionSelected = selectedDuplicateFamilyVersions?.some(
                        (item) => item.id === node.id
                    )

                    const isOpen = yield {
                        ...node,
                        id: node.id ?? '',
                        elementId: node?.elementId ?? '',
                        elementAddedToProjectId: node?.elementAddedToProjectId,
                        title: transformedElementTitle,
                        subtitle: transformedSubtitle,
                        treePath: node.treePath,
                        selected:
                            node.id === selectedElementIdentifier?.treePath ||
                            (node.elementId ? node.elementId : node?.id) ===
                                selectedElementIdentifier?.id,
                        nestingLevel,
                        onClick: node?.id?.startsWith('loadMoreButton')
                            ? onLoadMoreClick
                            : handleElementItemCardClick,
                        onBulkSelect: handleBulkAddRemoveElement,
                        selectedBulkAddElements: selectedBulkAddElements,
                        handleBPElementTreeChange: handleBPElementTreeChange,
                        defaultHeight: cardItemHeight,
                        isInProject: node.isInProject,
                        defaultWidth: isBetaWorkspaceToggleEnabled
                            ? node.isBestPractice || node.isGlobalBestPractice
                                ? // if MO enable with BP/GBP
                                  node.isBasedFromManufacturerOption
                                    ? ELEMENT_WIDTH_MO_WITH_BP
                                    : ELEMENT_WIDTH_NO_MO_WITH_BP
                                : // if MO enabled without BP/GBP
                                node.isBasedFromManufacturerOption
                                ? ELEMENT_WIDTH_MO_WITHOUT_BP
                                : ELEMENT_WIDTH_FULL
                            : calculatedTitleWidth,
                        isBetaWorkspaceToggleEnabled: isBetaWorkspaceToggleEnabled,
                        isLastSubAssembly: node.isLastSubAssembly,
                        isBestPractice: node.isBestPractice,
                        isGlobalBestPractice: node.isGlobalBestPractice,
                        isProjectOnly: node.isProjectOnly,
                        isImportedToProject: node.isImportedToProject,
                        isBasedFromImportedNode: node.isBasedFromImportedNode,
                        isBasedFromManufacturerOption:
                            node.isBasedFromManufacturerOption,
                        isBpPublished: node.isBpPublished,
                        importedToProjectId: node.importedToProjectId,
                        hasChildAssociation: node.hasChildAssociation,
                        hasSiblingAssociation: node.hasSiblingAssociation,
                        isAddedForAssociation: node.isAddedForAssociation,
                        selectedElementFormat: selectedElementFormat,
                        contentTeamFromStudioPage: contentTeamFromStudioPage,
                        elementType: node.elementType,
                        disableExpand:
                            contentTeamFromStudioPage &&
                            node.isLastSubAssembly &&
                            !pathname.includes('workspace'),
                        // expand tree path up to the selected item
                        isOpenByDefault:
                            ((node.id === selectedElementIdentifier?.treePath ||
                                (node.elementId ? node.elementId : node?.id) ===
                                    selectedElementIdentifier?.id) &&
                                node.children?.length!) ||
                            fromStudio
                                ? lastFamilyExpand || isExpand
                                : isExpand,
                        searchText: searchFilters?.[0]?.searchText ?? '',
                        loadChildren: onLoadChildren,
                        calledFromImportPage: calledFromImportPage,
                        selectedFile: selectedFile,
                        parentNodeId: node.parentNodeId,
                        isFromWorkSpacePage: isFromWorkSpacePage,
                        calledFromImportDuplicateFamily: calledFromImportDuplicateFamily,
                        isDuplicateFamilyVersionSelected: isDuplicateFamilyVersionSelected,
                        updateElementList: updateElementList,
                        /**TODO: This is the temporary code changes for CKE ProductType elements once it will be available we can remove some of the conditions */
                        parentFamilyId:
                            node.elementType === ProjectElementType.PRODUCTTYPE
                                ? parentElementItemNodes.find((item) =>
                                      item.childrenIds.includes(
                                          node.id as string
                                      )
                                  )?.familyViewElementId
                                : '',
                        iteration: node.iteration,
                    }

                    if (node.hasSubItems && isOpen) {
                        const subItems = node.children?.map((subItem) => {
                            return {
                                nestingLevel: nestingLevel + 1,
                                node: subItem,
                            } as StackElement
                        })
                        if (subItems) {
                            stack.unshift(...subItems)
                        }
                    }

                    /**TODO: This is the temporary code changes for CKE ProductType elements once it will be available we can remove some of the conditions */
                    if (
                        node.elementType === ProjectElementType.PRODUCTFAMILY &&
                        node.children?.length &&
                        !parentElementItemNodes.find(
                            (item) => item.id === node.id
                        )
                    ) {
                        parentElementItemNodes.push({
                            id: node.id as string,
                            elementId: node.elementId as string,
                            familyViewElementId: (node.elementAddedToProjectId
                                ? node.elementAddedToProjectId
                                : node.baseElementId) as string,
                            childrenIds: getChildNodeItems(node.children).map(
                                (item: ElementItemDataNode) => item.id as string
                            ),
                        })
                    }
                }
            }

            if (hiddenElementSizer.parentNode) {
                document.body.removeChild(hiddenElementSizer)
            }
        },
        [
            treeArray,
            selectedElementIdentifier,
            handleElementItemCardClick,
            onLoadMoreClick,
            famNumberFormat,
            searchFilters,
            isCollapsed,
            isNewDataLoading,
            onLoadChildren,
            calledFromImportPage,
            associationList,
            selectedElementId,
            selectedElementFormat,
            pathname,
            selectedBulkAddElements,
            calledFromImportDuplicateFamily,
            isOMWorkspace,
            isBetaWorkspaceToggleEnabled,
        ]
    )

    React.useEffect(() => {
        if (isBetaWorkspaceToggleEnabled) {
            //work around to slightly scroll the tree for card height and position adjustment
            const variableSizedTreeDiv = window.document.getElementById(
                'elementTreeViewContainer'
            )?.childNodes?.[0]?.childNodes?.[0] as HTMLDivElement

            if (!!variableSizedTreeDiv) {
                variableSizedTreeDiv.scrollTop =
                    variableSizedTreeDiv.scrollTop + 1
            }
        }
    }, [isBetaWorkspaceToggleEnabled])
    React.useEffect(() => {
        if (isNewDataLoading) {
            virtualTreeRef.current?.recomputeTree({
                useDefaultOpenness: true,
            })
        }
    }, [isNewDataLoading])
    const useScrollToPersistedElement = React.useCallback(() => {
        if (
            virtualTreeRef.current &&
            persistedElement?.treePath &&
            !hasScrolledIntoElement
        ) {
            // constants for readability
            const TREE_ELEMENT_HEIGHT = 54
            const viewPortWidth = window?.visualViewport?.width ?? 0
            const EXTRA_PADDING = viewPortWidth > 2300 ? 10 : 0
            const VISIBLE_ITEMS_THRESHOLD = 8
            const SCROLL_OFFSET = 3 // Offset to center the element in view
            // parse the persisted tree path
            const parentElementTreeArray = persistedElement?.treePath.split(
                '||'
            )
            const parentIndex = treeArray.findIndex(
                (item) => item.id === parentElementTreeArray[0]
            )
            const parentElement = treeArray.find(
                (item) => item.id === parentElementTreeArray[0]
            )
            // find child index within the parent's children
            const childIndex =
                getChildNodeItems(parentElement?.children)?.findIndex(
                    (item: ElementItemDataNode) =>
                        item?.id === persistedElement.treePath
                ) ?? 0
            // calculate element's combined index and scroll position
            const elementIndex = parentIndex + childIndex
            const containerHeight = Number(
                virtualTreeRef?.current?.props?.height
            )
            const scrollPosition =
                (elementIndex - SCROLL_OFFSET) *
                (TREE_ELEMENT_HEIGHT + EXTRA_PADDING)
            const isElementOutOfView = elementIndex > VISIBLE_ITEMS_THRESHOLD
            const isScrollNeeded = containerHeight < scrollPosition
            if (isElementOutOfView && isScrollNeeded) {
                virtualTreeRef.current?.scrollTo(scrollPosition)
                setHasScrolledIntoElement(true)
            }
        }
    }, [
        virtualTreeRef.current,
        persistedElement,
        treeArray,
        hasScrolledIntoElement,
        setHasScrolledIntoElement,
    ])
    React.useEffect(() => {
        // remove the selected bulkadd items that are added to project from the bulk add list
        if (!!treeArray && !!selectedBulkAddElements) {
            const allTreeElements = getChildNodeItems(treeArray)
            const temporaryBulkAddElementsArray = selectedBulkAddElements?.filter(
                (item) =>
                    allTreeElements?.some(
                        (element: ElementItemDataNode) =>
                            element?.id === item.treePath &&
                            !element?.isInProject
                    )
            )
            if (
                !isEqual(
                    selectedBulkAddElements,
                    temporaryBulkAddElementsArray
                ) &&
                !!allTreeElements?.length
            ) {
                setSelectedBulkAddElements?.(temporaryBulkAddElementsArray)
            }
        }
    }, [
        selectedBulkAddElements,
        treeArray,
        getChildNodeItems,
        setSelectedBulkAddElements,
    ])

    // The below useEffect is used to scroll persisted Tree elements that are not visible into view
    React.useEffect(() => {
        useScrollToPersistedElement()
    }, [useScrollToPersistedElement])

    React.useEffect(() => {
        if (parentNodeIdPendingBulkSelect) {
            // User requested a bulk select of the node's children
            const parentNode: ElementItemDataNode | undefined = getNodeById(
                treeArray,
                parentNodeIdPendingBulkSelect
            )
            if (
                parentNode &&
                !parentNode.isInProject &&
                parentNode.children &&
                parentNode.children.length
            ) {
                handleBulkAddRemoveElement(parentNode, 'all', true)
            }
        }
    }, [
        parentNodeIdPendingBulkSelect,
        // Exclude other dependencies because we only want this effect to run when shift+click bulk select is requested or finished.
        // Otherwise the bulk add/remove would be triggered on every tree update.
    ])

    // The below useEffect is used to check pass the selected element with updated state(isInProject: true/false) to persistPreviousAddedToProjectElement callback
    // This is used when we add/remove a selected element to a project
    React.useEffect(() => {
        if (selectedElementIdentifier && treeArray) {
            const parentElement:
                | ElementItemDataNode
                | undefined = treeArray?.find(
                (item) =>
                    item.id ==
                    selectedElementIdentifier?.treePath?.split('||')[0]
            )
            if (!parentElement) return
            const children = getChildNodeItems(parentElement?.children)
            const allTreePathItems: ElementItemDataNode[] = (
                children ?? []
            ).concat(parentElement)
            const selectedElement = allTreePathItems?.find(
                (item) => item.id === selectedElementIdentifier?.treePath
            )
            if (selectedElement)
                persistPreviousAddedToProjectElement(selectedElement!)
        }
    }, [
        selectedElementIdentifier,
        persistPreviousAddedToProjectElement,
        getChildNodeItems,
    ])

    return (
        <>
            {isOMWorkspace && !isBetaWorkspaceToggleEnabled && (
                <div className={classes.filterContainer}>
                    <OMVersionDropdown
                        handleChange={handleOMVersionSelectChange!}
                        value={selectedOMVersion}
                        options={omDropdownOptions}
                        multiple={false}
                    />
                </div>
            )}
            <div
                id="elementTreeViewContainer"
                data-testid="elementTreeViewContainer"
                className={classes.elementPanelContentContainer}
            >
                <Autosizer disableWidth>
                    {({ height }: { height: number }) => {
                        return (
                            <VariableSizeTree
                                data-testid="elementTreeView"
                                ref={virtualTreeRef}
                                height={
                                    // removing extra height from version control for OM
                                    isBetaWorkspaceToggleEnabled
                                        ? height - treeHeightForVersionControl
                                        : height
                                }
                                treeWalker={treeWalker}
                                width={
                                    contentTeamFromStudioPage ||
                                    isBetaWorkspaceToggleEnabled
                                        ? '100%'
                                        : '326px'
                                }
                            >
                                {ElementTreeViewNode}
                            </VariableSizeTree>
                        )
                    }}
                </Autosizer>
            </div>
        </>
    )
}

export const calculateTitleElementWidth = (
    isExpandable: boolean,
    nestingLevel: number,
    isBP: boolean | undefined
) => {
    const widthAdjustment = 16 * nestingLevel
    return 242 - widthAdjustment + (!isExpandable ? 21 : 0) - (!!isBP ? 22 : 0)
}

export default ElementTreeView
